import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiShield, FiUploadCloud, FiClock, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import styles from '../Kyc/Kyc.module.css';
import axiosClient from '../../api/axiosClient';

const KYB_DOCS = [
  { key: 'registration_certificate', label: 'Company Registration Certificate' },
  { key: 'gst_pan_proof', label: 'GST / PAN Proof' },
  { key: 'ubo_id_proof', label: "UBO's Government ID Proof" },
];

const STATUS_ICON: Record<string, React.ReactNode> = {
  pending: <FiClock />, approved: <FiCheckCircle />, rejected: <FiXCircle />,
};
const STATUS_COLOR: Record<string, string> = {
  pending: '#e8a86a', approved: '#5cb87d', rejected: '#e2534a',
};

const KybSection: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'company' | 'documents'>('company');

  const [profile, setProfile] = useState({
    companyName: '', registrationNumber: '', gstPan: '', companyAddress: '',
    incorporationCountry: '', uboName: '', uboDesignation: '', uboOwnershipPct: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [files, setFiles] = useState<Record<string, File | null>>({
    registration_certificate: null,
    gst_pan_proof: null,
    ubo_id_proof: null,
  });
  const [docSaving, setDocSaving] = useState(false);
  const [docError, setDocError] = useState('');
  const [uploadedKeys, setUploadedKeys] = useState<string[]>([]);
  const [profileStatus, setProfileStatus] = useState<string | null>(null);
  // An admin filled this section for the user: everything is read-only, changes go through Support.
  const [adminLocked, setAdminLocked] = useState(false);
  const CHANGE_REQUEST_URL = '/dashboard/support?subject=' + encodeURIComponent('KYB change request') +
    '&message=' + encodeURIComponent('Please update my KYB / company details: ');
  const [docStatuses, setDocStatuses] = useState<{ document_type: string; status: string }[]>([]);

  useEffect(() => {
    axiosClient.get('/kyb/profile').then(({ data }) => {
      setAdminLocked(!!data.adminLocked);
      if (data.profile) {
        setProfile({
          companyName: data.profile.company_name || '',
          registrationNumber: data.profile.registration_number || '',
          gstPan: data.profile.gst_pan || '',
          companyAddress: data.profile.company_address || '',
          incorporationCountry: data.profile.incorporation_country || '',
          uboName: data.profile.ubo_name || '',
          uboDesignation: data.profile.ubo_designation || '',
          uboOwnershipPct: data.profile.ubo_ownership_pct || '',
        });
        setProfileStatus(data.profile.status || 'pending');
      }
    }).catch(() => {});
    axiosClient.get('/kyb/documents').then(({ data }) => {
      setDocStatuses(data.documents || []);
      if (data.adminLocked) setAdminLocked(true);
    }).catch(() => {});
  }, []);

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axiosClient.post('/kyb/profile', profile);
      setSaved(true);
      toast.success('Company details saved.');
      setTab('documents');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not save company details.');
    } finally {
      setSaving(false);
    }
  };

  const handleDocUpload = async (key: string, label: string) => {
    const file = files[key];
    if (!file) return;
    setDocError('');
    setDocSaving(true);
    try {
      const formData = new FormData();
      formData.append('documentType', key);
      formData.append('document', file);
      await axiosClient.post('/kyb/documents', formData);
      setUploadedKeys((prev) => [...prev, key]);
      toast.success(`${label} uploaded.`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setDocSaving(false);
    }
  };

  return (
    <div>
      {(profileStatus || docStatuses.length > 0) && (
        <div className={styles.statusList} style={{ marginBottom: 16 }}>
          {profileStatus && (
            <div className={styles.statusChip} style={{ borderColor: STATUS_COLOR[profileStatus], color: STATUS_COLOR[profileStatus] }}>
              {STATUS_ICON[profileStatus]} Company profile — {profileStatus}
            </div>
          )}
          {docStatuses.map((d, i) => (
            <div key={i} className={styles.statusChip} style={{ borderColor: STATUS_COLOR[d.status], color: STATUS_COLOR[d.status] }}>
              {STATUS_ICON[d.status]} {KYB_DOCS.find((k) => k.key === d.document_type)?.label || d.document_type} — {d.status}
            </div>
          ))}
        </div>
      )}

      <div className={styles.tabs}>
        <button className={tab === 'company' ? styles.tabActive : styles.tab} onClick={() => setTab('company')}>
          Company details
        </button>
        <button className={tab === 'documents' ? styles.tabActive : styles.tab} onClick={() => setTab('documents')}>
          Documents
        </button>
      </div>

      {tab === 'company' && (
        <form className={styles.card} onSubmit={handleProfileSubmit}>
          {adminLocked ? (
            <div className={styles.lockBanner}>
              <FiShield /> Your KYB was filled in by an admin, so you cannot change it yourself.{' '}
              <a href={CHANGE_REQUEST_URL}>Send a change request</a> and an admin will update it.
            </div>
          ) : profileStatus === 'approved' && (
            <div className={styles.lockBanner}>
              <FiShield /> Your KYB is approved, so company details are locked.{' '}
              <a href="/dashboard/support">Raise a support ticket</a> if you need to update them.
            </div>
          )}
          <fieldset disabled={adminLocked || profileStatus === 'approved'} className={styles.grid}>
            <div className={styles.field}>
              <label>Company Name <span>*</span></label>
              <input required placeholder="Enter registered company name" value={profile.companyName} onChange={(e) => setProfile({ ...profile, companyName: e.target.value })} />
            </div>
            <div className={styles.field}>
              <label>Registration Number <span>*</span></label>
              <input required placeholder="Company registration / CIN number" value={profile.registrationNumber} onChange={(e) => setProfile({ ...profile, registrationNumber: e.target.value })} />
            </div>
            <div className={styles.field}>
              <label>GST / PAN of Company <span>*</span></label>
              <input required placeholder="Enter GST or PAN" value={profile.gstPan} onChange={(e) => setProfile({ ...profile, gstPan: e.target.value })} />
            </div>
            <div className={styles.field}>
              <label>Incorporation Country <span>*</span></label>
              <input required placeholder="Country of incorporation" value={profile.incorporationCountry} onChange={(e) => setProfile({ ...profile, incorporationCountry: e.target.value })} />
            </div>
            <div className={styles.field} style={{ gridColumn: '1 / -1' }}>
              <label>Company Address <span>*</span></label>
              <input required placeholder="Registered office address" value={profile.companyAddress} onChange={(e) => setProfile({ ...profile, companyAddress: e.target.value })} />
            </div>
            <div className={styles.field}>
              <label>UBO Name <span>*</span></label>
              <input required placeholder="Ultimate Beneficial Owner's name" value={profile.uboName} onChange={(e) => setProfile({ ...profile, uboName: e.target.value })} />
            </div>
            <div className={styles.field}>
              <label>UBO Designation <span>*</span></label>
              <input required placeholder="e.g. Managing Partner, Director" value={profile.uboDesignation} onChange={(e) => setProfile({ ...profile, uboDesignation: e.target.value })} />
            </div>
            <div className={styles.field}>
              <label>UBO Ownership % <span>*</span></label>
              <input required type="number" min={0} max={100} placeholder="e.g. 25" value={profile.uboOwnershipPct} onChange={(e) => setProfile({ ...profile, uboOwnershipPct: e.target.value })} />
            </div>
          </fieldset>

          <button type="submit" className={styles.submitBtn} disabled={saving || adminLocked || profileStatus === 'approved'}>
            {saving ? 'Saving…' : 'Save & Continue'}
          </button>

          <div className={styles.infoBox}>
            <FiShield />
            <div>
              <b>Why we ask this</b>
              <p>This confirms your business is legally registered and that its actual owners/decision-makers (UBOs) are properly identified, as required for institutional onboarding.</p>
            </div>
          </div>
          {saved && <div className={styles.savedNote}>Company details saved.</div>}
        </form>
      )}

      {tab === 'documents' && (
        <div className={styles.card}>
          {adminLocked && (
            <div className={styles.lockBanner}>
              <FiShield /> Your KYB documents were filled in by an admin, so this section is locked.{' '}
              <a href={CHANGE_REQUEST_URL}>Send a change request</a> and an admin will update it.
            </div>
          )}
          {docError && <div className={styles.errorNote}>{docError}</div>}
          <div className={styles.uploadGrid} style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
            {KYB_DOCS.map((d) => {
              const existingDoc = docStatuses.find((s) => s.document_type === d.key);
              const isLocked = adminLocked || (!!existingDoc && existingDoc.status !== 'rejected');
              return (
                <div className={styles.field} key={d.key}>
                  <label>{d.label} <span>*</span></label>
                  <label className={styles.dropzone} style={isLocked ? { opacity: .55, pointerEvents: 'none' } : undefined}>
                    <FiUploadCloud />
                    <span>
                      {isLocked
                        ? (existingDoc ? `${existingDoc.status[0].toUpperCase() + existingDoc.status.slice(1)}` : 'Locked')
                        : files[d.key] ? files[d.key]!.name : uploadedKeys.includes(d.key) ? 'Uploaded ✓' : 'Click to upload'}
                    </span>
                    <small>PDF, JPG, or PNG (Max 5MB)</small>
                    <input
                      type="file"
                      hidden
                      disabled={isLocked}
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setFiles((prev) => ({ ...prev, [d.key]: file }));
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className={styles.submitKycBtn}
                    style={{ marginTop: 8 }}
                    disabled={!files[d.key] || docSaving || isLocked}
                    onClick={() => handleDocUpload(d.key, d.label)}
                  >
                    {isLocked ? 'Locked' : uploadedKeys.includes(d.key) ? 'Re-upload' : 'Upload'}
                  </button>
                </div>
              );
            })}
          </div>
          <div className={styles.actionsRow} style={{ marginTop: 24 }}>
            <button type="button" className={styles.cancelBtn} onClick={() => navigate('/dashboard')}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default KybSection;